import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { loginMaster, loginUsername, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const u = (username || "").trim().toLowerCase();
    const p = (password || "").trim();
    if (!u || !p) {
      toast.error("Preencha usuário e senha");
      return;
    }
    setLoading(true);
    try {
      // O usuário master ("admin") usa endpoint dedicado (senha em env).
      // Demais usuários usam login por username no banco.
      if (u === "admin") {
        await loginMaster(u, p);
      } else {
        await loginUsername(u, p);
      }
      toast.success("Bem-vindo à AAB Repairs");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 || status === 403) {
        toast.error(detail || "Usuário ou senha incorretos");
      } else if (!err?.response) {
        toast.error("Sem conexão com o servidor. Verifique sua internet.");
      } else {
        toast.error(`Erro ${status}: ${detail || "tente novamente"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="login-page">
      {/* Left panel with branding only */}
      <div className="hidden lg:flex flex-col items-center justify-center w-1/2 border-r border-border p-12 relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          <BrandLogo size="lg" />
          <div className="text-[10px] uppercase text-muted-foreground tracking-[0.2em]">Management System</div>
        </div>
      </div>

      {/* Right panel with form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <BrandLogo size="md" />
          </div>

          <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Entrar</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Acesse o painel operacional da oficina.
          </p>

          <form onSubmit={submit} className="space-y-3" data-testid="master-login-form" autoComplete="off">
            {/* Honeypots para desativar autofill agressivo do navegador */}
            <input type="text" name="fakeuser" autoComplete="username" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
            <input type="password" name="fakepass" autoComplete="current-password" style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-wider text-muted-foreground">Usuário</Label>
              <Input
                id="username"
                name="aab-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onBlur={(e) => setUsername(e.target.value.trim())}
                className="mt-1"
                data-testid="username-input"
                placeholder=""
                autoComplete="off"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Senha</Label>
              <Input
                id="password"
                name="aab-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={(e) => setPassword(e.target.value.trim())}
                className="mt-1"
                data-testid="password-input"
                autoComplete="new-password"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                placeholder=""
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-btn">
              {loading ? "Entrando…" : "Entrar"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
